import { Component, EventEmitter, Input, Output, Inject, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Offer, paymentFrequencies, paymentTerms } from 'app/services/offers/offer.model';
import { DeleteOfferDialogComponent } from '../delete-offer-dialog/delete-offer-dialog.component';
import * as _ from 'lodash';
import { FormlyDialogComponent, FormlyDialogData } from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { UntypedFormGroup } from '@angular/forms';
import { OfferFormFields } from '../offer.formly-fields';
import { of } from 'rxjs';
import { ClipboardService } from 'ngx-clipboard';
import { RollbarService } from 'app/rollbar';
import Rollbar from 'rollbar';

@Component({
  selector: 'app-offer',
  templateUrl: './offer.component.html',
  styleUrls: ['./offer.component.scss']
})
export class OfferComponent implements OnChanges {

  @Input() offer: Offer;
  @Input() offerNumber: number;
  @Input() acceptedOfferId: number | null = null;
  @Input() dealStage: string;
  @Input() canEdit: boolean;
  @Input() canEditInactiveOffers = false;
  @Input() canDelete: boolean;
  @Input() readOnly: boolean = false;

  @Output() publish = new EventEmitter<number>();
  @Output() acceptedToggle = new EventEmitter<{ id: number, checked: boolean }>();
  @Output() expandToggle = new EventEmitter<number>();
  @Output() editOffer = new EventEmitter<{ offer: Offer }>();
  @Output() createOffer = new EventEmitter<{ offer: Offer }>();
  borrowerHasChosen: boolean;
  canDeleteOffer: boolean;
  canEditOffer: boolean;
  disableOffer: boolean;
  isPublished: boolean;
  offerExpiredFormatted: Date;
  offerFormFields = new OfferFormFields();
  offerRecievedFormatted: Date;
  paymentFrequencies = paymentFrequencies;
  paymentTerms = paymentTerms;

  constructor(
    @Inject(RollbarService) private rollbar: Rollbar,
    private dialog: MatDialog,
    private clipboard: ClipboardService
  ) { }

   ngOnChanges() {
     this.offerRecievedFormatted = new Date(this.offer.received);
     if (this.offer.expiration) {
       this.offerExpiredFormatted = new Date(this.offer.expiration);
     }
     this.setParams();
   }


  _borrowerHasChosen() {
    return !!(this.offer.borrowerAccepted || this.offer.borrowerDeclined);
  }

  _isPublished() {
    return !!this.offer.publishedToBorrower;
  }

  _disableOffer() {
    return !!this.acceptedOfferId && this.acceptedOfferId !== this.offer.id;
  }

  setParams() {
    this.borrowerHasChosen = this._borrowerHasChosen();
    this.disableOffer = this._disableOffer();
    this.isPublished = this._isPublished();

    this.canEditOffer = (
      this.canEdit
      && !this.offer.accepted
      && !this.isPublished
      && !this.disableOffer
      && !this.offer.offerLink
      && this.dealStage !== 'inactive'
      && this.dealStage !== 'funded'
    ) || this.canEditInactiveOffers;

    this.canDeleteOffer = (
      this.canDelete
      && !this.offer.accepted
      && !this.borrowerHasChosen
      && !this.offer.apiLogId
      && this.dealStage !== 'inactive'
      && this.dealStage !== 'funded'
    );

  }

  sendOfferToBorrowerPortal() {
    this.publish.emit(this.offer.id);
  }

  confirmDelete(): void {
    this.dialog.open(DeleteOfferDialogComponent, { data: this.offer.id });
  }

  handleDuplicate() {
    this.setCostType();
    const dialogData = this.formlyDialogData();
    _.extend(dialogData.formlyConfig.model, {
      id: null,
      accepted: null,
      name: null,
      offerLink: null,
      url: null,
      received: new Date(),
    });
    dialogData.title = 'Create Duplicate Offer';
    delete dialogData.formlyConfig.model.expiration;
    dialogData.ignoreDirty = true;

    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: dialogData,
      width: '60rem',
      panelClass: 'offer-form-dialog'
    });
    dialogRef.componentInstance.saveData.subscribe(data => {
      const offer = { ...data.formlyConfig.form.value };
      if (offer.costType) {
        offer[offer.costType] = offer.cost;
      }
      this.createOffer.emit({ ...offer });
      dialogRef.close();
    });
  }

  handleEdit() {
    this.setCostType();
    this.offer.received = new Date(this.offer.received);
    this.offer.expiration = this.offer.expiration ? new Date(this.offer.expiration) : null;
    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data: this.formlyDialogData(),
      width: '60rem',
      panelClass: 'offer-form-dialog'
    });
    // sub
    dialogRef.componentInstance.saveData.subscribe(data => {
      const offer = { ...data.formlyConfig.form.value };
      if (offer.costType) {
        offer[offer.costType] = offer.cost;
      }
      offer.id = this.offer.id;
      this.editOffer.emit({ offer: offer });
      dialogRef.close();
    });
  }

  acceptedToggleChange(checked: boolean) {
    this.acceptedToggle.emit({ id: this.offer.id, checked });
  }

  expandToggleEvent() {
    this.expandToggle.emit(this.offer.id);
  }

  setCostType() {
    if (this.offer.apr) {
      this.offer.costType = 'apr';
      this.offer.cost = this.offer.apr;
    } else if (this.offer.factor) {
      this.offer.costType = 'factor';
      this.offer.cost = this.offer.factor;
    } else if (this.offer.interestRate) {
      this.offer.costType = 'interest';
      this.offer.cost = this.offer.interestRate;
    }
  }

  copyUrl(url: string) {
    this.clipboard.copyFromContent(url);
  }

  formattedOfferPoints(offerPoints: string): number | void {
    try {
      if (offerPoints === '.') {
        return;
      }
      const offerPointsAsNumber = Number(offerPoints);
      return offerPointsAsNumber;
    } catch(e: unknown) {
      this.rollbar.error({error: e});
      return;
    }
  }

  private formlyDialogData(): FormlyDialogData {
    return {
      loading$: of(false),
      title: 'Edit Offer',
      formlyConfig: {
        model:  { ...this.offer },
        form: new UntypedFormGroup({}),
        fields: this.offer.loanProductType !== 'factoring' ? this.offerFormFields.getOfferFields
          : this.offerFormFields.getFactoringFields,
        options: {
          formState: {
            mainModel: { ...this.offer }
          }
        }
      }
    };
  }
}
